/**
 * The Dynamics board's drill-down URL vocabulary (dashboard design rules §4.2):
 * one place for the routes and search params the L1 sections and the L2
 * sub-routes share, so a donut segment, a "View all N →" link and the L2
 * `FilterRow` can never disagree about what `?code=` means.
 *
 * The aggregate routes (`/dynamics`, `/forms`, `/requests`, `/queues`) carry no
 * param that names a person — their backend exposes no user identifiers, and
 * the report's `TopUsersPain` / `HelpdeskTriage` pages have no equivalent. The
 * one exception is deliberate and gated: `/dynamics/investigate` carries
 * `?mail=` and `?activity=` for the F&O Performance Console's per-user layer
 * (concept §7 L2, D1/D7), which opens only against a reason the backend
 * audit-logs. See `./reason-gate.tsx`.
 */

/** The L1 board. */
export const DYNAMICS_PATH = "/dynamics";

/** L2 sub-route: every form the backend ranked, with its full percentiles. */
export const DYNAMICS_FORMS_PATH = "/dynamics/forms";

/** L2 sub-route: result codes, failing dependencies and the slowest requests. */
export const DYNAMICS_REQUESTS_PATH = "/dynamics/requests";

/** L2 sub-route: every Service Bus queue the namespace reported. */
export const DYNAMICS_QUEUES_PATH = "/dynamics/queues";

/**
 * L2 sub-route: the F&O Performance Console's per-user investigation — search a
 * person, see their lookback as a timeline, open one slow-interaction record.
 * Reason-gated (D7): every per-user request carries `?reason=` server-side and
 * is audit-logged; the page will not ask for a person without one.
 */
export const DYNAMICS_INVESTIGATE_PATH = "/dynamics/investigate";

/**
 * The pinpoint board: what to fix, in what order, and what it costs.
 *
 * An L1 board rather than an L2 list even though it hangs under `/dynamics`:
 * it answers a different question from the board above it. `/dynamics` says
 * *what is slow*; this says *what is worth a ticket*, ranked by the person-hours
 * of waiting each finding causes. Investigator-gated like the two per-person
 * screens — it names no people, but a ranked list of which company is worst is
 * an operational judgement about named parts of the business.
 */
export const DYNAMICS_PINPOINT_PATH = "/dynamics/pinpoint";

/**
 * L2 sub-route: the user-report queue — the people who pressed "this app is
 * slow right now" in the extension popup.
 *
 * Its own screen rather than a block on the investigate page: it is a work
 * queue with its own filters (`?window=`, `?status=`) and its own selection
 * (`?report=`), and it is usually where an investigation *starts*, so burying
 * it under a person's timeline made the first step the last thing on the page.
 * Listing it needs no reason; opening one report does, and its drawer says so.
 */
export const DYNAMICS_REPORTS_PATH = "/dynamics/reports";

/** In-page anchors the L0 KPI tiles link down to. */
export const AVAILABILITY_ANCHOR = "fo-availability";
export const EXPERIENCE_ANCHOR = "fo-experience";
export const REQUESTS_ANCHOR = "fo-requests";
export const INTEGRATION_ANCHOR = "fo-integration";
/** The slow-interaction section (classification, coverage, worst forms/sites). */
export const SLOW_ANCHOR = "fo-slow";
/** The user-report queue's section on `/dynamics/reports`. */
export const REPORTS_ANCHOR = "fo-reports";
/** The ranked findings section on `/dynamics/pinpoint`. */
export const PINPOINT_FINDINGS_ANCHOR = "fo-pinpoint-findings";
/** The per-company load table on `/dynamics/pinpoint`. */
export const PINPOINT_COMPANIES_ANCHOR = "fo-pinpoint-companies";

/**
 * Single-value param carrying the console lookback (`1h|6h|24h|3d|7d`). Shared
 * by the board's slow section and the investigate page, so a drill from one to
 * the other keeps the same window.
 */
export const LOOKBACK_PARAM = "lookback";

/** Single-value param: the person under investigation (mail), investigate page only. */
export const MAIL_PARAM = "mail";

/**
 * Single-value param: the `FoConsolePinpointFinding.id` whose evidence drawer is
 * open (pinpoint board only). The id is the backend's own stable slug, so the
 * drawer survives a refresh and a shared link opens on the same finding.
 */
export const FINDING_PARAM = "finding";

/**
 * Single-value param: the legal-entity code the pinpoint board is filtered to,
 * set by clicking a row in the company table. Absent means every company.
 *
 * Deliberately not `FORM_PARAM`'s sibling on the investigate page: this one
 * scopes an aggregate board, never a per-person view.
 */
export const COMPANY_PARAM = "company";

/** Single-value param: the `activity_id` whose §5 record drawer is open. */
export const ACTIVITY_PARAM = "activity";

/** Single-value param: pre-filter the investigate page's slow grid to one form. */
export const FORM_PARAM = "form";

/** Single-value param: pre-filter the investigate page's slow grid to one Cato site. */
export const SITE_PARAM = "site";

/**
 * Single-value param: which triage state the report queue is filtered to
 * (`new|triaged|closed`); absent means every state. Reports page only.
 */
export const REPORT_STATUS_PARAM = "status";

/**
 * Single-value param carrying the report queue's window (`24h|7d|30d|90d`).
 *
 * Deliberately **not** `lookback`: the queue is a Postgres table purged at 90
 * days, while `lookback` scopes the five trailing KQL windows
 * `FoSlowInteractions()` is served for. One param for both would silently
 * shorten the queue to an hour whenever someone narrowed the console.
 */
export const REPORT_WINDOW_PARAM = "window";

/** Single-value param: the id of the user report whose detail drawer is open. */
export const REPORT_PARAM = "report";

/* The board's screens are NOT listed here. They are registry entries in
   `components/nav-sections.ts` carrying `parent: "/dynamics"`, and the sidebar
   renders them. A second list in the feature was what the retired
   `DynamicsSubNav` read from, and it had already drifted: it named two of the
   six screens. One nav model, one list. */

/** Single-value param carrying the environment whose detail drawer is open. */
export const ENV_DRAWER_PARAM = "env";

/** Single-value param carrying the queue whose detail drawer is open. */
export const QUEUE_DRAWER_PARAM = "queue";

/** Multi-value param carrying selected result codes (comma-joined). */
export const RESULT_CODE_PARAM = "code";
